import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { JobCandidateMeetingRecordsListComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-candidate-meeting-records-list/job-vacancy-candidate-meeting-records-list.component';
import { JobCandidatePhaseTracklineComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-candidate-phase-trackline/job-vacancy-candidate-phase-trackline.component';
import { JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { JobCandidate } from 'src/app/models/job_candidate.model';
import { JobVacancy, JobVacancyPhase } from 'src/app/models/job_vacancy.model';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-job-vacancy-candidate-detail',
  templateUrl: './job-vacancy-candidate-detail.component.html',
  styleUrl: './job-vacancy-candidate-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    JobCandidatePhaseTracklineComponent,
    JobCandidateMeetingRecordsListComponent,
  ],
})
export class JobCandidateDetailComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly is_discarded = signal(false);
  readonly job_candidate = signal<JobCandidate | null>(null);
  readonly job_vacancy = signal<JobVacancy | null>(null);
  readonly sorted_phases = signal<JobVacancyPhase[]>([]);

  readonly candidate_form = this.form_builder.group({
    full_name: ['', Validators.required],
    email: ['', [Validators.email]],
    phone: [''],
    linkedin_url: [''],
    notes: [''],
    job_vacancy_phase_id: [null as number | null],
  });

  protected job_vacancy_id = 0;

  protected job_candidate_id = 0;

  ngOnInit(): void {
    this.job_vacancy_id = Number(this.activated_route.snapshot.paramMap.get('job_vacancy_id'));
    this.job_candidate_id = Number(this.activated_route.snapshot.paramMap.get('id'));

    if (!this.job_vacancy_id || !this.job_candidate_id) {
      return;
    }

    this.loadJobVacancy();
    this.loadJobCandidate();
  }

  discardCandidate(): void {
    const job_candidate = this.job_candidate();

    if (!job_candidate || this.is_discarded()) {
      return;
    }

    if (!confirm(`¿Descartar a "${job_candidate.full_name}"?`)) {
      return;
    }

    this.api_service
      .discardJobCandidate(job_candidate.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.snack_bar.open('Candidato descartado', undefined, { duration: 3000 });
          void this.router.navigate(['/employees', 'rrhh', 'job-vacancies', this.job_vacancy_id]);
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al descartar el candidato.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  saveCandidate(): void {
    const job_candidate = this.job_candidate();

    if (!job_candidate || this.saving() || this.candidate_form.invalid || this.is_discarded()) {
      return;
    }

    this.saving.set(true);

    const raw = this.candidate_form.getRawValue();

    this.api_service
      .updateJobCandidate(job_candidate.id, {
        full_name: raw.full_name ?? '',
        email: raw.email || null,
        phone: raw.phone || null,
        linkedin_url: raw.linkedin_url || null,
        notes: raw.notes || null,
        job_vacancy_phase_id: raw.job_vacancy_phase_id,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.saving.set(false);
          this.snack_bar.open('Candidato actualizado', undefined, { duration: 3000 });
          this.loadJobCandidate();
        },
        error: (err: { error?: { error?: string } }): void => {
          this.saving.set(false);
          const message = err?.error?.error ?? 'Error al actualizar el candidato.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private loadJobVacancy(): void {
    this.api_service
      .getJobVacancy(this.job_vacancy_id, { with: 'job_vacancy_phases' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (job_vacancy: JobVacancy): void => {
          this.job_vacancy.set(job_vacancy);
          const phases_with_labels =
            job_vacancy.job_vacancy_phases?.map(phase => ({
              ...phase,
              type_label: JobVacancyPhaseTypeEnumLabels[phase.type] ?? phase.type,
            })) ?? [];

          this.sorted_phases.set(
            [...phases_with_labels].sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order)
          );
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private loadJobCandidate(): void {
    this.loading.set(true);

    this.api_service
      .getJobCandidate(this.job_candidate_id, {
        with: 'active_job_vacancy_candidate_phase_progress.job_vacancy_phase,job_vacancy_candidate_phase_progress.job_vacancy_phase,user',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (job_candidate: JobCandidate): void => {
          this.job_candidate.set(job_candidate);
          this.is_discarded.set(this.computeIsDiscarded(job_candidate));

          if (this.is_discarded()) {
            this.candidate_form.disable();
          } else {
            this.candidate_form.enable();
          }

          this.candidate_form.patchValue({
            full_name: job_candidate.full_name,
            email: job_candidate.email ?? '',
            phone: job_candidate.phone ?? '',
            linkedin_url: job_candidate.linkedin_url ?? '',
            notes: job_candidate.notes ?? '',
            job_vacancy_phase_id: job_candidate.active_job_vacancy_candidate_phase_progress?.job_vacancy_phase_id ?? null,
          });
          this.loading.set(false);
          this.change_detector_ref.markForCheck();
        },
        error: (): void => {
          this.loading.set(false);
          this.snack_bar.open('No se pudo cargar el candidato.', undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private computeIsDiscarded(job_candidate: JobCandidate): boolean {
    const has_open_progress = job_candidate.job_vacancy_candidate_phase_progress?.some(
      progress => progress.completed_at === null && progress.discarded_at === null
    );

    if (has_open_progress) {
      return false;
    }

    return job_candidate.job_vacancy_candidate_phase_progress?.some(progress => progress.discarded_at !== null) ?? false;
  }
}
